import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Lock,
  Plus,
  Search,
  Tags,
  Trash2,
  Power,
  PowerOff,
  X,
  ExternalLink,
  Globe,
  Layers,
  List,
  ListTree,
  Pencil,
  Columns3,
  GitMerge,
} from 'lucide-react';

import {
  useAdminTagCmsQuery,
  useAdminTagDetailQuery,
  useAdminTagEntitiesQuery,
  useAdminTagsQuery,
} from '@/client/api/tags/tag.query';
import {
  useBulkAssignTagEntitiesMutation,
  useCreateTagMutation,
  useDeleteTagMutation,
  useMergeTagMutation,
  useUpdateTagMutation,
} from '@/client/api/tags/tag.mutation';
import { CMSTag, CMS_LOCALES } from '@/client/types/cms';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import useDebounce from '@/client/hooks/useDebounce';
import Pagination from '@/client/components/common/Pagination';
import SortableTh from '@/client/components/common/SortableTh';
import SearchableSelect from '@/client/components/admin/SearchableSelect';
import { useEntityTypesQuery } from '@/client/api/listing-management/entity-types.query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const selectCls =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
const textareaCls =
  'flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
const filterLabelCls = 'block h-4 text-xs leading-4 text-gray-500';
const filterFieldCls = 'space-y-1';

const isTruthy = (v: unknown) => v === true || v === 1 || v === '1';

const LOCALE_LABELS: Record<string, string> = {
  en_GB: 'English (UK)',
  en_US: 'English (US)',
  fr_FR: 'French (France)',
  es_ES: 'Spanish (Spain)',
  pt_BR: 'Portuguese (Brazil)',
  de_DE: 'German (Germany)',
};

const localeFilterOptions = [
  { value: '', label: 'All locales' },
  ...CMS_LOCALES.filter(Boolean).map((code) => ({
    value: code,
    label: LOCALE_LABELS[code] ? `${LOCALE_LABELS[code]} (${code})` : code,
  })),
];

const COLUMN_OPTIONS = [
  { key: 'parent', label: 'Parent' },
  { key: 'scope', label: 'Scope' },
  { key: 'flags', label: 'Flags' },
  { key: 'usage', label: 'Usage' },
  { key: 'status', label: 'Status' },
  { key: 'modified', label: 'Modified' },
] as const;

type ColumnKey = (typeof COLUMN_OPTIONS)[number]['key'];

const ALL_COLUMNS = COLUMN_OPTIONS.map((c) => c.key) as ColumnKey[];
const COLUMN_PREF_KEY = 'ff-admin.tags.columns';

const readColumnPrefs = (): ColumnKey[] => {
  if (typeof window === 'undefined') return ALL_COLUMNS;
  try {
    const raw = window.localStorage.getItem(COLUMN_PREF_KEY);
    if (!raw) return ALL_COLUMNS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return ALL_COLUMNS;
    const valid = parsed.filter((k): k is ColumnKey => ALL_COLUMNS.includes(k));
    return valid;
  } catch {
    return ALL_COLUMNS;
  }
};

/** `etid_json` arrives as either a JSON string or an already-parsed array. */
const parseEtidList = (raw: unknown): number[] | null => {
  if (raw == null || raw === '') return null;
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(value)) return null;
  const ids = value.map((v) => Number(v)).filter((n) => Number.isFinite(n));
  return ids.length > 0 ? ids : null;
};

type TagFilters = {
  active: string;
  protected: string;
  in_use: string;
  autogen: string;
  locale: string;
  etid: string;
  sort_by: string;
  sort_order: string;
  hierarchy: string;
};

/** Nest a flat tag page into depth-ordered rows using tagid_parent. */
const nestTagsByParent = (tags: CMSTag[]): { t: CMSTag; depth: number }[] => {
  const byId = new Map(tags.map((t) => [t.tagid, t]));
  const children = new Map<number | 'root', CMSTag[]>();
  for (const t of tags) {
    const parentInPage =
      t.tagid_parent != null && byId.has(Number(t.tagid_parent))
        ? Number(t.tagid_parent)
        : null;
    const key: number | 'root' = parentInPage ?? 'root';
    const bucket = children.get(key) ?? [];
    bucket.push(t);
    children.set(key, bucket);
  }
  const out: { t: CMSTag; depth: number }[] = [];
  const walk = (parentKey: number | 'root', depth: number) => {
    const nodes = [...(children.get(parentKey) ?? [])].sort((a, b) =>
      (a.name || '').localeCompare(b.name || ''),
    );
    for (const n of nodes) {
      out.push({ t: n, depth });
      walk(n.tagid, depth + 1);
    }
  };
  walk('root', 0);
  return out;
};

type TagFormState = {
  name: string;
  description: string;
  tagid_parent: string;
  locale: string;
  etid_json: string;
  name_other: string;
  attributes: string;
  active: boolean;
  protected: boolean;
  in_use: boolean;
  autogen: boolean;
};

const emptyForm = (): TagFormState => ({
  name: '',
  description: '',
  tagid_parent: '',
  locale: '',
  etid_json: '',
  name_other: '',
  attributes: '',
  active: true,
  protected: false,
  in_use: true,
  autogen: false,
});

const formFromTag = (t: CMSTag): TagFormState => ({
  name: t.name ?? '',
  description: t.description ?? '',
  tagid_parent: t.tagid_parent != null ? String(t.tagid_parent) : '',
  locale: t.locale ?? '',
  etid_json: t.etid_json != null ? JSON.stringify(t.etid_json) : '',
  name_other: t.name_other != null ? JSON.stringify(t.name_other, null, 2) : '',
  attributes: t.attributes != null ? JSON.stringify(t.attributes, null, 2) : '',
  active: t.active == null ? true : isTruthy(t.active),
  protected: isTruthy(t.protected),
  in_use: t.in_use == null ? true : isTruthy(t.in_use),
  autogen: isTruthy(t.autogen),
});

const parseJsonField = (raw: string, label: string): { ok: true; value: unknown } | { ok: false; error: string } => {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, value: null };
  try {
    return { ok: true, value: JSON.parse(trimmed) };
  } catch {
    return { ok: false, error: `${label} must be valid JSON.` };
  }
};

const FlagPill = ({
  on,
  label,
  tone = 'neutral',
}: {
  on: boolean;
  label: string;
  tone?: 'neutral' | 'amber' | 'violet' | 'red';
}) => {
  if (!on) return null;
  const tones = {
    neutral: 'bg-gray-100 text-gray-700',
    amber: 'bg-amber-50 text-amber-800 border border-amber-200',
    violet: 'bg-violet-50 text-violet-800',
    red: 'bg-red-50 text-red-700',
  };
  return (
    <span className={cn('inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium', tones[tone])}>
      {label}
    </span>
  );
};

const TagsAdminPage = () => {
  useBreadcrumbs([
    { label: 'Content', href: '#' },
    { label: 'Tags' },
  ]);

  const managePerm = useHasPermission('tag.manage');
  const protectedPerm = useHasPermission('tag.protected.manage');
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;
  const canManageProtected = protectedPerm.isSuperAdmin || protectedPerm.hasPermission;

  const {
    page,
    limit,
    searchTerm,
    debouncedSearchTerm,
    filters,
    handlePageChange,
    handleLimitChange,
    handleSearch,
    handleFilterChange,
    resetFilters,
  } = useListFilters<TagFilters>({
    initialFilters: {
      active: '',
      protected: '',
      in_use: '',
      autogen: '',
      locale: '',
      etid: '',
      sort_by: 'name',
      sort_order: 'asc',
      hierarchy: '',
    },
    initialLimit: 25,
  });

  const hierarchy = filters.hierarchy === 'true';

  const offset = (page - 1) * limit;
  const queryParams = useMemo(
    () => ({
      q: debouncedSearchTerm || undefined,
      limit,
      offset,
      include_inactive: true,
      include_usage: true,
      active: filters.active === '' ? undefined : filters.active === '1',
      protected: filters.protected === '' ? undefined : filters.protected === '1',
      in_use: filters.in_use === '' ? undefined : filters.in_use === '1',
      autogen: filters.autogen === '' ? undefined : filters.autogen === '1',
      locale: filters.locale || undefined,
      etid: filters.etid || undefined,
      sort_by: filters.sort_by || 'name',
      sort_order: (filters.sort_order as 'asc' | 'desc') || 'asc',
    }),
    [debouncedSearchTerm, limit, offset, filters],
  );

  const { data, isLoading, isError, refetch } = useAdminTagsQuery(queryParams);
  const { data: entityTypesData } = useEntityTypesQuery(false);
  const createMutation = useCreateTagMutation();
  const updateMutation = useUpdateTagMutation();
  const deleteMutation = useDeleteTagMutation();
  const mergeMutation = useMergeTagMutation();
  const bulkAssignMutation = useBulkAssignTagEntitiesMutation();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CMSTag | null>(null);
  const [form, setForm] = useState<TagFormState>(emptyForm);
  const [detailTagid, setDetailTagid] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CMSTag | null>(null);
  const [mergeSource, setMergeSource] = useState<CMSTag | null>(null);
  const [mergeTarget, setMergeTarget] = useState<CMSTag | null>(null);
  const [mergeSearch, setMergeSearch] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [bulkEids, setBulkEids] = useState('');
  const [entitiesPage, setEntitiesPage] = useState(1);
  const [cmsPage, setCmsPage] = useState(1);
  const [detailTab, setDetailTab] = useState<'overview' | 'entities' | 'cms'>('overview');
  const [columns, setColumns] = useState<ColumnKey[]>(readColumnPrefs);
  const debouncedMergeSearch = useDebounce(mergeSearch.trim(), 300);

  const { data: mergeTargetsData, isLoading: mergeTargetsLoading } = useAdminTagsQuery(
    {
      q: debouncedMergeSearch || undefined,
      active: true,
      include_inactive: false,
      limit: 100,
      sort_by: 'name',
      sort_order: 'asc',
    },
    mergeSource != null,
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(columns));
    } catch {
      /* preference persistence is best-effort */
    }
  }, [columns]);

  const showCol = (key: ColumnKey) => columns.includes(key);
  const columnCount = 3 + columns.length;

  const openDetail = (tagid: number) => {
    setDetailTagid(tagid);
    setEntitiesPage(1);
    setCmsPage(1);
  };

  const { data: parentCatalog } = useAdminTagsQuery(
    { limit: 200, include_inactive: true, sort_by: 'name', sort_order: 'asc' },
    editorOpen,
  );

  const tags = data?.tags ?? [];
  // Staging may still omit `pagination.total` (older tag list contract only
  // returned page `count`). Never treat page size as the catalog total.
  const serverTotal = data?.pagination?.total;
  const hasServerTotal = typeof serverTotal === 'number' && Number.isFinite(serverTotal);
  const pageFull = tags.length === limit;
  const hasMore = !hasServerTotal && pageFull;
  const total = hasServerTotal
    ? serverTotal
    : offset + tags.length;

  const parentNameById = useMemo(() => {
    const map = new Map<number, string>();
    tags.forEach((t) => map.set(t.tagid, t.name));
    (parentCatalog?.tags ?? []).forEach((t) => map.set(t.tagid, t.name));
    return map;
  }, [tags, parentCatalog]);

  const displayRows = useMemo(() => {
    if (hierarchy) return nestTagsByParent(tags);
    return tags.map((t) => ({
      t,
      depth: t.tagid_parent != null ? 1 : 0,
    }));
  }, [tags, hierarchy]);

  const parentPickerOptions = useMemo(() => {
    const catalog = parentCatalog?.tags ?? tags;
    const opts = catalog
      .filter((t) => !editing || t.tagid !== editing.tagid)
      .map((t) => ({
        value: String(t.tagid),
        label: `${t.name} (#${t.tagid})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: '', label: 'No parent (root)' }, ...opts];
  }, [parentCatalog, tags, editing]);

  const mergeTargetOptions = useMemo(() => {
    const byId = new Map<number, CMSTag>();
    if (mergeTarget) byId.set(mergeTarget.tagid, mergeTarget);
    (mergeTargetsData?.tags ?? []).forEach((tag) => byId.set(tag.tagid, tag));
    return [...byId.values()]
      .filter((tag) => tag.tagid !== mergeSource?.tagid)
      .map((tag) => ({
        value: String(tag.tagid),
        label: `${tag.name} (#${tag.tagid})`,
      }));
  }, [mergeSource?.tagid, mergeTarget, mergeTargetsData]);

  const entityTypeList = useMemo(() => {
    const raw = (entityTypesData as any)?.entity_types
      ?? (entityTypesData as any)?.data?.entity_types
      ?? (Array.isArray(entityTypesData) ? entityTypesData : []);
    return Array.isArray(raw) ? raw.filter((t: any) => t?.etid != null) : [];
  }, [entityTypesData]);

  const entityTypeNameById = useMemo(() => {
    const map = new Map<number, string>();
    entityTypeList.forEach((t: any) => {
      map.set(
        Number(t.etid),
        t.name_friendly || t.name_system || t.name_menu || `Type #${t.etid}`,
      );
    });
    return map;
  }, [entityTypeList]);

  const entityTypeOptions = useMemo(() => {
    const opts = entityTypeList
      .map((t: any) => ({
        value: String(t.etid),
        label: `${entityTypeNameById.get(Number(t.etid))} (#${t.etid})`,
      }))
      .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));
    return [{ value: '', label: 'All entity types' }, ...opts];
  }, [entityTypeList, entityTypeNameById]);

  const detailQuery = useAdminTagDetailQuery(detailTagid, { include_usage: true });
  const entitiesQuery = useAdminTagEntitiesQuery(
    detailTagid,
    { limit: 20, offset: (entitiesPage - 1) * 20 },
    detailTagid != null,
  );
  const cmsQuery = useAdminTagCmsQuery(
    detailTagid,
    { limit: 20, offset: (cmsPage - 1) * 20 },
    detailTagid != null,
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setEditorOpen(true);
  };

  const openEdit = (t: CMSTag) => {
    setEditing(t);
    setForm(formFromTag(t));
    setEditorOpen(true);
  };

  const openMerge = (t: CMSTag) => {
    setMergeSource(t);
    setMergeTarget(null);
    setMergeSearch('');
  };

  const toggleSort = (field: string) => {
    if (filters.sort_by === field) {
      handleFilterChange('sort_order', filters.sort_order === 'asc' ? 'desc' : 'asc');
    } else {
      handleFilterChange('sort_by', field);
      handleFilterChange('sort_order', 'asc');
    }
  };

  const buildPayload = (): Partial<CMSTag> | null => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return null;
    }
    const etid = parseJsonField(form.etid_json, 'Compatible entity types');
    if (!etid.ok) {
      toast.error(etid.error);
      return null;
    }
    const nameOther = parseJsonField(form.name_other, 'Alternate names');
    if (!nameOther.ok) {
      toast.error(nameOther.error);
      return null;
    }
    const attrs = parseJsonField(form.attributes, 'Attributes');
    if (!attrs.ok) {
      toast.error(attrs.error);
      return null;
    }
    if (form.protected && !canManageProtected) {
      toast.error('You lack permission to manage protected tags');
      return null;
    }
    return {
      name: form.name.trim(),
      description: form.description.trim() || null,
      tagid_parent: form.tagid_parent ? Number(form.tagid_parent) : null,
      locale: form.locale.trim() || null,
      etid_json: (etid.value as number[] | null) ?? null,
      name_other: (nameOther.value as Record<string, string> | null) ?? null,
      attributes: (attrs.value as Record<string, unknown> | null) ?? null,
      active: form.active,
      protected: form.protected,
      in_use: form.in_use,
      autogen: form.autogen,
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;
    try {
      if (editing) {
        await updateMutation.mutateAsync({ tagid: editing.tagid, ...payload });
        toast.success('Tag updated');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Tag created');
      }
      setEditorOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'Save failed');
    }
  };

  const handleToggleActive = async (t: CMSTag, active: boolean) => {
    if (isTruthy(t.protected) && !canManageProtected) {
      toast.error('Protected tag — elevated permission required');
      return;
    }
    try {
      await updateMutation.mutateAsync({ tagid: t.tagid, active });
      toast.success(active ? 'Tag activated' : 'Tag deactivated');
    } catch (e: any) {
      toast.error(e?.message ?? 'Update failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (isTruthy(deleteTarget.protected) && !canManageProtected) {
      toast.error('Protected tag — elevated permission required');
      return;
    }
    try {
      await deleteMutation.mutateAsync({ tagid: deleteTarget.tagid });
      toast.success('Tag deactivated');
      setDeleteTarget(null);
      if (detailTagid === deleteTarget.tagid) setDetailTagid(null);
    } catch (e: any) {
      toast.error(e?.message ?? 'Delete failed');
    }
  };

  const handleMerge = async () => {
    if (!mergeSource || !mergeTarget) return;
    if (
      (isTruthy(mergeSource.protected) || isTruthy(mergeTarget.protected))
      && !canManageProtected
    ) {
      toast.error('Protected tag — elevated permission required');
      return;
    }
    try {
      const result = await mergeMutation.mutateAsync({
        sourceTagid: mergeSource.tagid,
        targetTagid: mergeTarget.tagid,
      });
      const assignments = result.assignments;
      const moved =
        assignments.entities.moved
        + assignments.cms_pages.moved
        + assignments.users.moved;
      const deduplicated =
        assignments.entities.deduplicated
        + assignments.cms_pages.deduplicated
        + assignments.users.deduplicated;
      toast.success(
        `Merged into ${result.target_name}: ${moved} assignment(s) moved`
          + (deduplicated ? `, ${deduplicated} duplicate(s) collapsed` : ''),
      );
      setMergeSource(null);
      setMergeTarget(null);
      setMergeSearch('');
      setSelected((prev) => prev.filter((tagid) => tagid !== mergeSource.tagid));
      if (detailTagid === mergeSource.tagid) setDetailTagid(mergeTarget.tagid);
    } catch (e: any) {
      toast.error(e?.message ?? 'Merge failed');
    }
  };

  const handleBulkActive = async (active: boolean) => {
    if (!selected.length) return;
    try {
      await Promise.all(
        selected.map((tagid) => updateMutation.mutateAsync({ tagid, active })),
      );
      toast.success(`${selected.length} tag(s) ${active ? 'activated' : 'deactivated'}`);
      setSelected([]);
    } catch (e: any) {
      toast.error(e?.message ?? 'Bulk update failed');
    }
  };

  const handleBulkAssign = async (mode: 'add' | 'remove') => {
    if (!detailTagid) return;
    const eids = bulkEids
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!eids.length) {
      toast.error('Enter one or more entity IDs');
      return;
    }
    try {
      const result = await bulkAssignMutation.mutateAsync({
        tagid: detailTagid,
        add: mode === 'add' ? eids : [],
        remove: mode === 'remove' ? eids : [],
      });
      const failures = result?.failures?.length ?? result?.data?.failures?.length ?? 0;
      const added = result?.added ?? result?.data?.added ?? 0;
      const removed = result?.removed ?? result?.data?.removed ?? 0;
      if (failures) toast.warning(`Completed with ${failures} failure(s)`);
      else toast.success(mode === 'add' ? `Assigned to ${added} entit(y/ies)` : `Removed from ${removed}`);
      setBulkEids('');
    } catch (e: any) {
      toast.error(e?.message ?? 'Bulk assignment failed');
    }
  };

  const hasActiveFilters =
    !!filters.active ||
    !!filters.protected ||
    !!filters.in_use ||
    !!filters.autogen ||
    !!filters.locale ||
    !!filters.etid ||
    !!debouncedSearchTerm;

  const detail = detailQuery.data;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="mb-1 flex items-center gap-3">
            <Tags className="h-7 w-7 text-blue-700" />
            <h1 className="text-2xl font-bold text-gray-900">Tags</h1>
          </div>
          <p className="ml-10 text-sm text-gray-500">
            Global taxonomy workspace — catalog, flags, usage, and entity assignment.
          </p>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            New tag
          </Button>
        )}
      </div>

      <div className="mb-4 space-y-3 rounded-lg border bg-white p-4 shadow">
        <div className="flex flex-wrap items-end gap-3">
          <div className={cn(filterFieldCls, 'min-w-[220px] flex-1')}>
            <Label className={filterLabelCls}>Search</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                className="h-9 pl-9"
                placeholder="Name, alternate names, or ID…"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>
          {(
            [
              ['active', 'Status', [['', 'All'], ['1', 'Active'], ['0', 'Inactive']]],
              ['protected', 'Protected', [['', 'All'], ['1', 'Protected'], ['0', 'Open']]],
              ['in_use', 'Assignable', [['', 'All'], ['1', 'Assignable'], ['0', 'Anchor only']]],
              ['autogen', 'Source', [['', 'All'], ['1', 'Autogen'], ['0', 'Manual']]],
            ] as const
          ).map(([key, label, options]) => (
            <div key={key} className={cn(filterFieldCls, 'w-36')}>
              <Label className={filterLabelCls}>{label}</Label>
              <select
                className={selectCls}
                value={filters[key]}
                onChange={(e) => handleFilterChange(key, e.target.value)}
              >
                {options.map(([v, l]) => (
                  <option key={v || 'all'} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
          ))}
          <div className={cn(filterFieldCls, 'w-48')}>
            <Label className={filterLabelCls}>Locale</Label>
            <SearchableSelect
              options={localeFilterOptions}
              value={filters.locale}
              onChange={(v) => handleFilterChange('locale', v)}
              placeholder="All locales"
              icon={Globe}
              className="w-full"
            />
          </div>
          <div className={cn(filterFieldCls, 'w-56')}>
            <Label className={filterLabelCls}>Entity type</Label>
            <SearchableSelect
              options={entityTypeOptions}
              value={filters.etid}
              onChange={(v) => handleFilterChange('etid', v)}
              placeholder="All entity types"
              icon={Layers}
              className="w-full"
            />
          </div>
          <div className="ml-auto flex flex-wrap items-end gap-2">
            {hasActiveFilters && (
              <div className={filterFieldCls}>
                <Label className={cn(filterLabelCls, 'invisible select-none')} aria-hidden>
                  Clear
                </Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 gap-1"
                  onClick={resetFilters}
                >
                  <X className="h-3.5 w-3.5" /> Clear
                </Button>
              </div>
            )}
            <div className={filterFieldCls}>
              <Label className={cn(filterLabelCls, 'invisible select-none')} aria-hidden>
                View
              </Label>
              <Button
                variant="outline"
                size="sm"
                className="h-9 gap-2"
                onClick={() => handleFilterChange('hierarchy', hierarchy ? '' : 'true')}
                title={
                  hierarchy
                    ? 'Show flat sorted list'
                    : 'Nest children under parents on this page'
                }
              >
                {hierarchy ? <List className="h-4 w-4" /> : <ListTree className="h-4 w-4" />}
                {hierarchy ? 'Flat list' : 'Tree view'}
              </Button>
            </div>
            <div className={filterFieldCls}>
              <Label className={cn(filterLabelCls, 'invisible select-none')} aria-hidden>
                Columns
              </Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 gap-2">
                    <Columns3 className="h-4 w-4" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {COLUMN_OPTIONS.map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={columns.includes(col.key)}
                      onCheckedChange={(checked) =>
                        setColumns((prev) =>
                          checked
                            ? ALL_COLUMNS.filter((k) => k === col.key || prev.includes(k))
                            : prev.filter((k) => k !== col.key),
                        )
                      }
                      onSelect={(e) => e.preventDefault()}
                    >
                      {col.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {canManage && selected.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm">
            <span className="font-medium text-blue-900">{selected.length} selected</span>
            <Button size="sm" variant="outline" onClick={() => handleBulkActive(true)}>
              Activate
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleBulkActive(false)}>
              Deactivate
            </Button>
            {selected.length === 2 && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => {
                  const [a, b] = selected;
                  const source = tags.find((t) => t.tagid === a);
                  const target = tags.find((t) => t.tagid === b);
                  if (!source) {
                    toast.error('Could not resolve the selected source tag on this page');
                    return;
                  }
                  setMergeSource(source);
                  setMergeTarget(target ?? null);
                  setMergeSearch(target?.name ?? '');
                }}
              >
                <GitMerge className="h-3.5 w-3.5" />
                Merge…
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setSelected([])}>
              Clear selection
            </Button>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        {isError && (
          <div className="border-b border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            Failed to load tags.{' '}
            <button className="underline" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-10 px-3 py-3">
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={tags.length > 0 && selected.length === tags.length}
                  onChange={(e) =>
                    setSelected(e.target.checked ? tags.map((t) => t.tagid) : [])
                  }
                  disabled={!canManage}
                />
              </th>
              <SortableTh
                label="Name"
                field="name"
                sortBy={filters.sort_by}
                sortOrder={filters.sort_order}
                onToggle={toggleSort}
              />
              {showCol('parent') && (
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Parent</th>
              )}
              {showCol('scope') && (
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Scope</th>
              )}
              {showCol('flags') && (
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Flags</th>
              )}
              {showCol('usage') && (
                <SortableTh
                  label="Usage"
                  field="usage"
                  sortBy={filters.sort_by}
                  sortOrder={filters.sort_order}
                  onToggle={toggleSort}
                />
              )}
              {showCol('status') && (
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
              )}
              {showCol('modified') && (
                <SortableTh
                  label="Modified"
                  field="date_modified"
                  sortBy={filters.sort_by}
                  sortOrder={filters.sort_order}
                  onToggle={toggleSort}
                />
              )}
              <th className="w-px px-3 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading &&
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  {[...Array(columnCount)].map((__, j) => (
                    <td key={j} className="px-4 py-3">
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))}
            {!isLoading && tags.length === 0 && (
              <tr>
                <td colSpan={columnCount} className="px-6 py-16 text-center text-sm text-gray-500">
                  No tags found.
                </td>
              </tr>
            )}
            {!isLoading &&
              displayRows.map(({ t, depth }) => {
                const active = t.active == null || isTruthy(t.active);
                const parentId = t.tagid_parent != null ? Number(t.tagid_parent) : null;
                const parentName =
                  parentId != null
                    ? parentNameById.get(parentId) ?? `Tag #${parentId}`
                    : null;
                const scopeEtids = parseEtidList(t.etid_json);
                const scopeNames = scopeEtids?.map(
                  (id) => entityTypeNameById.get(id) ?? `Type #${id}`,
                );
                return (
                  <tr key={t.tagid} className="hover:bg-gray-50">
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selected.includes(t.tagid)}
                        disabled={!canManage}
                        onChange={(e) =>
                          setSelected((prev) =>
                            e.target.checked
                              ? [...prev, t.tagid]
                              : prev.filter((id) => id !== t.tagid),
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className="flex items-start gap-1.5"
                        style={{ paddingLeft: Math.min(depth, 6) * 16 }}
                      >
                        {depth > 0 && (
                          <span className="mt-0.5 shrink-0 font-mono text-gray-300" aria-hidden>
                            └
                          </span>
                        )}
                        <div className="min-w-0">
                          <button
                            className="text-left text-sm font-medium hover:text-blue-700"
                            onClick={() => openDetail(t.tagid)}
                          >
                            {t.name}
                          </button>
                          <p className="font-mono text-xs text-gray-400">#{t.tagid}</p>
                          {!showCol('parent') && parentName && parentId != null && (
                            <p className="mt-0.5 text-[11px] text-gray-500">
                              under{' '}
                              <button
                                type="button"
                                className="font-medium text-gray-700 hover:text-blue-700 hover:underline"
                                onClick={() => openDetail(parentId)}
                              >
                                {parentName}
                              </button>
                            </p>
                          )}
                          {t.description && (
                            <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">
                              {t.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    {showCol('parent') && (
                      <td className="px-4 py-3 text-xs">
                        {parentName && parentId != null ? (
                          <button
                            type="button"
                            className="text-left text-gray-700 hover:text-blue-700 hover:underline"
                            onClick={() => openDetail(parentId)}
                          >
                            {parentName}
                          </button>
                        ) : (
                          <span className="text-gray-400">Root</span>
                        )}
                      </td>
                    )}
                    {showCol('scope') && (
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {scopeNames ? (
                          <span title={scopeNames.join(', ')}>
                            {scopeNames.slice(0, 2).join(', ')}
                            {scopeNames.length > 2 && (
                              <span className="text-gray-400">
                                {' '}
                                +{scopeNames.length - 2} more
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400">All types</span>
                        )}
                      </td>
                    )}
                    {showCol('flags') && (
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          <FlagPill on={isTruthy(t.protected)} label="protected" tone="amber" />
                          <FlagPill on={t.in_use != null && !isTruthy(t.in_use)} label="anchor" />
                          <FlagPill on={isTruthy(t.autogen)} label="autogen" tone="violet" />
                          {t.locale && <FlagPill on label={t.locale} />}
                        </div>
                      </td>
                    )}
                    {showCol('usage') && (
                      <td className="px-4 py-3 text-xs text-gray-600">
                        <div>
                          {typeof t.entity_count === 'number' ? t.entity_count : '—'} entities
                        </div>
                        <div className="text-gray-400">
                          {typeof t.cms_count === 'number' ? t.cms_count : '—'} CMS
                        </div>
                      </td>
                    )}
                    {showCol('status') && (
                      <td className="px-4 py-3 text-xs">
                        {active ? (
                          <span className="text-emerald-700">Active</span>
                        ) : (
                          <span className="text-amber-700">Inactive</span>
                        )}
                      </td>
                    )}
                    {showCol('modified') && (
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {t.date_modified
                          ? new Date(t.date_modified).toLocaleDateString()
                          : '—'}
                      </td>
                    )}
                    <td className="w-px whitespace-nowrap px-3 py-3 text-right">
                      {canManage && (
                        <div className="flex items-center justify-end gap-0.5">
                          <button
                            type="button"
                            title={active ? 'Deactivate' : 'Activate'}
                            aria-label={`${active ? 'Deactivate' : 'Activate'} ${t.name}`}
                            onClick={() => handleToggleActive(t, !active)}
                            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                          >
                            {active ? (
                              <PowerOff className="h-4 w-4" />
                            ) : (
                              <Power className="h-4 w-4 text-emerald-600" />
                            )}
                          </button>
                          <button
                            type="button"
                            title="Edit tag"
                            aria-label={`Edit ${t.name}`}
                            onClick={() => openEdit(t)}
                            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-blue-700"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            title="Merge into another tag"
                            aria-label={`Merge ${t.name}`}
                            onClick={() => openMerge(t)}
                            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-violet-700"
                          >
                            <GitMerge className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            title="Deactivate (soft delete)"
                            aria-label={`Delete ${t.name}`}
                            onClick={() => setDeleteTarget(t)}
                            className="rounded p-1.5 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        <div className="border-t px-4 py-3">
          <Pagination
            total={total}
            page={page}
            itemsPerPage={limit}
            limit={limit}
            setPage={handlePageChange}
            setItemsPerPage={handleLimitChange}
            showSelect
            selectOptions={[10, 25, 50, 100]}
            hasMore={hasMore}
            compact
          />
        </div>
      </div>

      {/* Create / edit */}
      <Sheet open={editorOpen} onOpenChange={setEditorOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{editing ? 'Edit tag' : 'New tag'}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <textarea
                rows={3}
                className={textareaCls}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Parent tag</Label>
                <SearchableSelect
                  options={parentPickerOptions}
                  value={form.tagid_parent}
                  onChange={(v) => setForm((p) => ({ ...p, tagid_parent: v }))}
                  placeholder="No parent (root)"
                  icon={ListTree}
                  className="w-full"
                />
                <p className="text-[11px] text-gray-500">
                  Parents define taxonomy hierarchy (e.g. Amenities → Bathroom).
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Locale</Label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'No locale' },
                    ...localeFilterOptions.filter((o) => o.value),
                  ]}
                  value={form.locale}
                  onChange={(v) => setForm((p) => ({ ...p, locale: v }))}
                  placeholder="Select locale"
                  icon={Globe}
                  className="w-full"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Compatible entity types (JSON array of etids)</Label>
              <Input
                value={form.etid_json}
                onChange={(e) => setForm((p) => ({ ...p, etid_json: e.target.value }))}
                placeholder='e.g. [2, 7]'
                className="font-mono text-xs"
              />
              <p className="text-[11px] text-gray-500">Empty means compatible with all types.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Alternate names (JSON object)</Label>
              <textarea
                rows={2}
                className={cn(textareaCls, 'font-mono text-xs')}
                value={form.name_other}
                onChange={(e) => setForm((p) => ({ ...p, name_other: e.target.value }))}
                placeholder='{"fr": "…"}'
              />
            </div>
            <div className="space-y-1.5">
              <Label>Attributes (JSON)</Label>
              <textarea
                rows={2}
                className={cn(textareaCls, 'font-mono text-xs')}
                value={form.attributes}
                onChange={(e) => setForm((p) => ({ ...p, attributes: e.target.value }))}
              />
            </div>
            <div className="space-y-3 rounded-md border p-3">
              {(
                [
                  ['active', 'Active', 'Inactive tags stay in the catalog but are not assignable.'],
                  ['in_use', 'Assignable', 'Off = hierarchy/anchor only (cannot assign to entities).'],
                  ['autogen', 'Autogenerated', 'Created by system pipelines rather than editors.'],
                  ['protected', 'Protected', 'Requires tag.protected.manage to mutate.'],
                ] as const
              ).map(([key, label, help]) => (
                <div key={key} className="flex items-start justify-between gap-3">
                  <div>
                    <Label className="flex items-center gap-1.5">
                      {key === 'protected' && <Lock className="h-3.5 w-3.5 text-amber-600" />}
                      {label}
                    </Label>
                    <p className="text-[11px] text-gray-500">{help}</p>
                  </div>
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4"
                    checked={form[key]}
                    disabled={key === 'protected' && !canManageProtected}
                    onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.checked }))}
                  />
                </div>
              ))}
            </div>
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="w-full"
            >
              Save
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Detail */}
      <Sheet
        open={detailTagid != null}
        onOpenChange={(o) => {
          if (!o) setDetailTagid(null);
        }}
      >
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {detail?.name ?? (detailQuery.isLoading ? 'Loading…' : 'Tag')}
              {isTruthy(detail?.protected) && <Lock className="h-4 w-4 text-amber-600" />}
            </SheetTitle>
          </SheetHeader>
          {detail && (
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap gap-2 text-xs">
                <FlagPill on={detail.active == null || isTruthy(detail.active)} label="active" />
                <FlagPill on={!(detail.active == null || isTruthy(detail.active))} label="inactive" tone="amber" />
                <FlagPill on={isTruthy(detail.protected)} label="protected" tone="amber" />
                <FlagPill on={detail.in_use != null && !isTruthy(detail.in_use)} label="anchor only" />
                <FlagPill on={isTruthy(detail.autogen)} label="autogen" tone="violet" />
              </div>
              <p className="text-sm text-gray-600">{detail.description || 'No description.'}</p>
              <div className="grid grid-cols-3 gap-2 rounded-md border bg-gray-50 p-3 text-center text-xs">
                <div>
                  <div className="text-lg font-semibold">{detail.entity_count ?? 0}</div>
                  Entities
                </div>
                <div>
                  <div className="text-lg font-semibold">{detail.cms_count ?? 0}</div>
                  CMS
                </div>
                <div>
                  <div className="text-lg font-semibold">{detail.total_count ?? 0}</div>
                  Total
                </div>
              </div>
              {canManage && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => openEdit(detail)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      handleToggleActive(detail, !(detail.active == null || isTruthy(detail.active)))
                    }
                  >
                    {(detail.active == null || isTruthy(detail.active)) ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => openMerge(detail)}
                  >
                    <GitMerge className="h-3.5 w-3.5" />
                    Merge
                  </Button>
                </div>
              )}

              <div>
                <div className="mb-3 grid grid-cols-3 gap-1 rounded-md border bg-gray-50 p-1">
                  {(['overview', 'entities', 'cms'] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setDetailTab(tab)}
                      className={cn(
                        'rounded px-2 py-1.5 text-xs font-medium capitalize',
                        detailTab === tab ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-800',
                      )}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
                {detailTab === 'overview' && (
                <div className="space-y-2 text-xs text-gray-600">
                  <div>ID: <span className="font-mono">#{detail.tagid}</span></div>
                  <div>
                    Parent:{' '}
                    {detail.tagid_parent != null ? (
                      <button
                        type="button"
                        className="font-medium text-blue-700 hover:underline"
                        onClick={() => setDetailTagid(Number(detail.tagid_parent))}
                      >
                        {parentNameById.get(Number(detail.tagid_parent)) ??
                          `#${detail.tagid_parent}`}
                      </button>
                    ) : (
                      '—'
                    )}
                  </div>
                  <div>Locale: {detail.locale || '—'}</div>
                  <div>
                    Synonyms:{' '}
                    {detail.synonyms?.length ? detail.synonyms.join(', ') : '—'}
                  </div>
                  <div>
                    etid_json:{' '}
                    <code className="rounded bg-gray-100 px-1">
                      {detail.etid_json != null ? JSON.stringify(detail.etid_json) : '—'}
                    </code>
                  </div>
                  <div>Modified: {detail.date_modified || '—'}</div>
                </div>
                )}
                {detailTab === 'entities' && (
                <div className="space-y-3">
                  {canManage && (
                    <div className="space-y-2 rounded-md border p-3">
                      <Label className="text-xs">Bulk assign entity IDs</Label>
                      <textarea
                        rows={2}
                        className={cn(textareaCls, 'font-mono text-xs')}
                        placeholder="e.g. 101, 202 303"
                        value={bulkEids}
                        onChange={(e) => setBulkEids(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={bulkAssignMutation.isPending}
                          onClick={() => handleBulkAssign('add')}
                        >
                          Add
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={bulkAssignMutation.isPending}
                          onClick={() => handleBulkAssign('remove')}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="max-h-72 space-y-1 overflow-y-auto">
                    {entitiesQuery.isLoading && <Skeleton className="h-16 w-full" />}
                    {(entitiesQuery.data?.entities ?? []).map((ent: any) => (
                      <div
                        key={ent.eid}
                        className="flex items-center justify-between rounded border px-2 py-1.5 text-xs"
                      >
                        <div>
                          <div className="font-medium">{ent.name || `Entity #${ent.eid}`}</div>
                          <div className="font-mono text-gray-400">#{ent.eid}</div>
                        </div>
                        <Link
                          to={`/admin/listings/${ent.eid}`}
                          className="text-blue-600 hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    ))}
                    {!entitiesQuery.isLoading &&
                      (entitiesQuery.data?.entities ?? []).length === 0 && (
                        <p className="text-xs text-gray-500">No entities assigned.</p>
                      )}
                  </div>
                  <Pagination
                    total={entitiesQuery.data?.pagination?.total ?? 0}
                    page={entitiesPage}
                    itemsPerPage={20}
                    limit={20}
                    setPage={setEntitiesPage}
                    setItemsPerPage={() => undefined}
                    showButtons
                  />
                </div>
                )}
                {detailTab === 'cms' && (
                <div className="space-y-3">
                  <div className="max-h-72 space-y-1 overflow-y-auto">
                    {cmsQuery.isLoading && <Skeleton className="h-16 w-full" />}
                    {(cmsQuery.data?.cms_pages ?? []).map((p: any) => (
                      <div
                        key={p.cmsoid}
                        className="flex items-center justify-between rounded border px-2 py-1.5 text-xs"
                      >
                        <div>
                          <div className="font-medium">{p.title || `Page #${p.cmsoid}`}</div>
                          <div className="text-gray-400">
                            {p.ctype} · {p.slug}
                          </div>
                        </div>
                        <Link
                          to={`/admin/content/pages/${p.cmsoid}`}
                          className="text-blue-600 hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                    ))}
                    {!cmsQuery.isLoading && (cmsQuery.data?.cms_pages ?? []).length === 0 && (
                      <p className="text-xs text-gray-500">No CMS pages assigned.</p>
                    )}
                  </div>
                  <Pagination
                    total={cmsQuery.data?.pagination?.total ?? 0}
                    page={cmsPage}
                    itemsPerPage={20}
                    limit={20}
                    setPage={setCmsPage}
                    setItemsPerPage={() => undefined}
                    showButtons
                  />
                </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={!!mergeSource}
        onOpenChange={(open) => {
          if (!open && !mergeMutation.isPending) {
            setMergeSource(null);
            setMergeTarget(null);
            setMergeSearch('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merge “{mergeSource?.name}” into another tag?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently consolidates the source into the target. Review how existing
              assignments are handled before confirming.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Target tag</Label>
              <SearchableSelect
                options={mergeTargetOptions}
                value={mergeTarget ? String(mergeTarget.tagid) : ''}
                onChange={(value) => {
                  const tagid = Number(value);
                  const selectedTag =
                    (mergeTarget?.tagid === tagid ? mergeTarget : null)
                    ?? (mergeTargetsData?.tags ?? []).find((tag) => tag.tagid === tagid);
                  setMergeTarget(selectedTag ?? null);
                }}
                onSearchChange={setMergeSearch}
                placeholder={mergeTargetsLoading ? 'Loading tags…' : 'Search active tags…'}
                icon={Tags}
              />
            </div>

            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-950">
              <ul className="list-disc space-y-1 pl-4">
                <li>Entity, CMS page, and user assignments move to the target.</li>
                <li>If the target is already assigned, the duplicate is collapsed.</li>
                <li>The source becomes inactive, but its assignment rows remain as history.</li>
                <li>The source name and its aliases become searchable target synonyms.</li>
                <li>Direct child tags are reparented to the target.</li>
              </ul>
            </div>

            {(isTruthy(mergeSource?.protected) || isTruthy(mergeTarget?.protected)) && (
              <p className="text-xs text-amber-700">
                One of these tags is protected — tag.protected.manage is required.
              </p>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={mergeMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                handleMerge();
              }}
              disabled={
                !mergeTarget
                || mergeMutation.isPending
                || (
                  (isTruthy(mergeSource?.protected) || isTruthy(mergeTarget?.protected))
                  && !canManageProtected
                )
              }
            >
              {mergeMutation.isPending ? 'Merging…' : 'Merge tags'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isTruthy(deleteTarget?.protected) ? 'Deactivate protected tag?' : 'Deactivate tag?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Soft-deactivates <strong>{deleteTarget?.name}</strong> (active=0). Assignments are
              kept; hard-delete is refused while the tag is in use.
              {isTruthy(deleteTarget?.protected) && (
                <span className="mt-2 block text-amber-700">
                  This tag is protected — tag.protected.manage is required.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={
                isTruthy(deleteTarget?.protected) && !canManageProtected
              }
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TagsAdminPage;
